import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const DAY_MINUTES = 24 * 60;

function parseDeltaMinutes(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed);
}

function parseTimeValue(value) {
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    !Number.isInteger(hours)
    || !Number.isInteger(minutes)
    || hours < 0
    || hours > 24
    || minutes < 0
    || minutes > 59
    || (hours === 24 && minutes !== 0)
  ) {
    return null;
  }

  return (hours * 60) + minutes;
}

function formatTimeValue(totalMinutes) {
  const minutes = Math.round(Number(totalMinutes));
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > DAY_MINUTES) {
    return '';
  }

  if (minutes === DAY_MINUTES) {
    return '24:00';
  }

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
    minutes % 60,
  ).padStart(2, '0')}`;
}

function formatTimeRange(startValue, endValue) {
  if (!startValue || !endValue) {
    return '-';
  }

  return `${startValue}-${endValue}`;
}

function getValidShiftItems(request) {
  if (request?.mode !== 'selection') {
    return [];
  }

  return (request?.context?.items ?? []).filter((item) => (
    Number.isFinite(Number(item?.occurrenceStartMinutes))
    && Number.isFinite(Number(item?.occurrenceEndMinutes))
    && Number(item.occurrenceEndMinutes) > Number(item.occurrenceStartMinutes)
  ));
}

function buildShiftTimeReference(request) {
  const items = getValidShiftItems(request);
  if (items.length > 0) {
    return {
      endMinutes: Math.max(...items.map((item) => Number(item.occurrenceEndMinutes))),
      itemCount: items.length,
      kind: items.length > 1 ? 'selection' : 'workflow',
      startMinutes: Math.min(...items.map((item) => Number(item.occurrenceStartMinutes))),
    };
  }

  const startMinutes = Number(request?.context?.occurrenceStartMinutes);
  const endMinutes = Number(request?.context?.occurrenceEndMinutes);
  if (
    Number.isFinite(startMinutes)
    && Number.isFinite(endMinutes)
    && endMinutes > startMinutes
  ) {
    return {
      endMinutes,
      itemCount: 1,
      kind: 'workflow',
      startMinutes,
    };
  }

  return null;
}

function buildShiftBounds(request, reference) {
  const items = getValidShiftItems(request);
  if (items.length > 0) {
    return {
      maxDeltaMinutes: Math.min(
        ...items.map((item) => DAY_MINUTES - Number(item.occurrenceEndMinutes)),
      ),
      minDeltaMinutes: Math.max(
        ...items.map((item) => -Number(item.occurrenceStartMinutes)),
      ),
    };
  }

  if (!reference) {
    return null;
  }

  return {
    maxDeltaMinutes: DAY_MINUTES - reference.endMinutes,
    minDeltaMinutes: -reference.startMinutes,
  };
}

function buildTitle(request) {
  if (request?.mode === 'day') {
    return 'Alle Workflows verschieben';
  }
  if (request?.mode === 'selection') {
    return 'Ausgewählte Workflows verschieben';
  }

  return 'Workflow verschieben';
}

function buildIntro(request) {
  if (request?.mode === 'day') {
    return 'Verschiebt alle sichtbaren Workflows des aktiven Tages um die angegebene Zeit.';
  }
  if (request?.mode === 'selection') {
    const count = Number(request?.context?.selectionCount ?? 0);
    return count > 1
      ? `Verschiebt ${count} ausgewählte Workflows synchron um die angegebene Zeit.`
      : 'Verschiebt den ausgewählten Workflow um die angegebene Zeit.';
  }

  const name = String(request?.context?.workflowName ?? '').trim();
  return name
    ? `Verschiebt "${name}" um die angegebene Zeit.`
    : 'Verschiebt diesen Workflow um die angegebene Zeit.';
}

function isSingleRegularWorkflow(request) {
  return request?.mode === 'workflow'
    && request?.context?.workflowEntity === 'regular-workflows';
}

function isMixedWorkflowSelection(request) {
  if (request?.mode !== 'selection') {
    return false;
  }

  const items = request?.context?.items ?? [];
  return items.some((item) => item.workflowType === 'day')
    && items.some((item) => item.workflowType === 'regular');
}

function buildRegularScopeLabels(request) {
  if (isSingleRegularWorkflow(request)) {
    return {
      day: 'Nur dieses Vorkommen verschieben',
      rule: 'Ganze Routine verschieben',
    };
  }

  return {
    day: 'Nur Vorkommen an diesem Tag verschieben',
    rule: 'Ganze Routinen dauerhaft verschieben',
  };
}

function buildShiftSubmitLabel(request, regularScope, busy) {
  if (busy) {
    return 'Verschiebe…';
  }

  if (!isSingleRegularWorkflow(request)) {
    if (request?.mode === 'selection' && request?.context?.containsRegular) {
      return regularScope === 'rule'
        ? 'Routinen verschieben'
        : 'Vorkommen verschieben';
    }

    return 'Verschieben';
  }

  return regularScope === 'rule'
    ? 'Routine verschieben'
    : 'Vorkommen verschieben';
}

function InfoTooltipButton({ label, title }) {
  return (
    <button
      type="button"
      className="timeline-shift-info-button"
      aria-label={label}
      data-bs-toggle="tooltip"
      data-bs-placement="top"
      data-bs-title={title}
    >
      <i className="bi bi-info-circle" aria-hidden="true" />
    </button>
  );
}

function hideTimelineShiftModal(modalEl, onClose) {
  const ModalCtor = window.bootstrap?.Modal;
  const modal = modalEl && ModalCtor && typeof ModalCtor.getInstance === 'function'
    ? ModalCtor.getInstance(modalEl)
    : null;

  if (modal) {
    modal.hide();
    return;
  }

  onClose?.();
}

function isTimelineShiftCopyCreatedEvent(event) {
  const detail = event?.detail ?? {};
  if (String(detail.source ?? '') !== 'timeline-shift-copy') {
    return false;
  }

  const entity = String(detail.entity ?? '').replace(/_/g, '-');
  return entity === 'day-workflows' || entity === 'regular-workflows';
}

export default function TimelineShiftModal({
  request,
  busy = false,
  onClose,
  onPreviewDeltaChange,
  onSubmit,
}) {
  const modalRef = useRef(null);
  const [deltaValue, setDeltaValue] = useState('15');
  const [regularScope, setRegularScope] = useState('day');
  const [activeTimeField, setActiveTimeField] = useState(null);
  const [timeDrafts, setTimeDrafts] = useState({ end: '', start: '' });
  const isOpen = Boolean(request);
  const needsRegularScope = (
    (request?.mode === 'workflow' && request?.context?.workflowType === 'regular')
    || (request?.mode === 'selection' && request?.context?.containsRegular)
  );
  const mixedWorkflowSelection = isMixedWorkflowSelection(request);
  const canCopyShiftedWorkflow = request?.mode === 'workflow'
    && (
      request?.context?.workflowEntity === 'day-workflows'
      || request?.context?.workflowEntity === 'regular-workflows'
    );
  const parsedDelta = useMemo(() => parseDeltaMinutes(deltaValue), [deltaValue]);
  const shiftTimeReference = useMemo(() => buildShiftTimeReference(request), [request]);
  const shiftBounds = useMemo(
    () => buildShiftBounds(request, shiftTimeReference),
    [request, shiftTimeReference],
  );
  const deltaWithinBounds = (
    !shiftBounds
    || !Number.isInteger(parsedDelta)
    || (
      parsedDelta >= shiftBounds.minDeltaMinutes
      && parsedDelta <= shiftBounds.maxDeltaMinutes
    )
  );
  const activeTimeDraftInvalid = Boolean(
    activeTimeField
    && !Number.isInteger(parseTimeValue(timeDrafts[activeTimeField])),
  );
  const canSubmit = Number.isInteger(parsedDelta)
    && parsedDelta !== 0
    && deltaWithinBounds
    && !activeTimeDraftInvalid
    && !busy;
  const regularScopeLabels = buildRegularScopeLabels(request);
  const sourceIsRegularWorkflow = isSingleRegularWorkflow(request);
  const shiftSubmitLabel = buildShiftSubmitLabel(request, regularScope, busy);
  const shiftedStartValue = shiftTimeReference && Number.isInteger(parsedDelta)
    ? formatTimeValue(shiftTimeReference.startMinutes + parsedDelta)
    : '';
  const shiftedEndValue = shiftTimeReference && Number.isInteger(parsedDelta)
    ? formatTimeValue(shiftTimeReference.endMinutes + parsedDelta)
    : '';
  const sourceStartValue = shiftTimeReference
    ? formatTimeValue(shiftTimeReference.startMinutes)
    : '';
  const sourceEndValue = shiftTimeReference
    ? formatTimeValue(shiftTimeReference.endMinutes)
    : '';
  const sourceTimeRangeLabel = formatTimeRange(sourceStartValue, sourceEndValue);
  const targetTimeRangeLabel = formatTimeRange(shiftedStartValue, shiftedEndValue);
  const displayedStartValue = activeTimeField === 'start'
    ? timeDrafts.start
    : shiftedStartValue;
  const displayedEndValue = activeTimeField === 'end'
    ? timeDrafts.end
    : shiftedEndValue;
  const shiftOutOfBounds = Boolean(
    shiftBounds
    && Number.isInteger(parsedDelta)
    && !deltaWithinBounds,
  );
  const shiftTimeHelp = shiftTimeReference?.kind === 'selection'
    ? 'Die Zeiten beziehen sich auf den gesamten ausgewählten Zeitraum.'
    : 'Die Zeiten beziehen sich auf das sichtbare Workflow-Vorkommen.';

  const deltaHelp = 'Positive Werte verschieben später, negative Werte früher.';
  const targetTimeHelp = `${shiftTimeHelp} Setzt du Start oder Ende, wird die Minutenverschiebung automatisch berechnet.`;
  const scopeHelp = isSingleRegularWorkflow
    ? '„Nur dieses Vorkommen“ verschiebt nur den sichtbaren Termin dieser Routine. „Ganze Routine“ verschiebt die wiederkehrende Routine.'
    : '„Vorkommen“ verschiebt nur die sichtbaren Termine am aktiven Tag. „Ganze Routinen“ verschiebt die wiederkehrenden Routinen.';
  const dayModeHelp = 'Routinen werden für diesen Tag als Tages-Workflow gespeichert und erhalten automatisch eine Ausnahme für den aktiven Tag.';
  const validationMessage = activeTimeDraftInvalid
    ? 'Bitte gib eine Zeit im Format HH:MM ein.'
    : (
      shiftOutOfBounds
        ? 'Die neue Zeit liegt außerhalb des aktiven Tages.'
        : ''
    );

  useEffect(() => {
    if (!request) {
      return;
    }

    const initialDelta = Number(request.initialDeltaMinutes);
    setDeltaValue(Number.isInteger(initialDelta) && initialDelta !== 0
      ? String(initialDelta)
      : '15');
    setRegularScope('day');
    setActiveTimeField(null);
    setTimeDrafts({ end: '', start: '' });
  }, [request]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const modalEl = modalRef.current;
    const ModalCtor = window.bootstrap?.Modal;
    if (!modalEl || !ModalCtor) {
      return undefined;
    }

    const modal =
      typeof ModalCtor.getOrCreateInstance === 'function'
        ? ModalCtor.getOrCreateInstance(modalEl, {
          backdrop: 'static',
          keyboard: true,
          focus: true,
        })
        : new ModalCtor(modalEl, {
          backdrop: 'static',
          keyboard: true,
          focus: true,
        });

    function handleHidden() {
      onClose?.();
    }

    modalEl.addEventListener('hidden.bs.modal', handleHidden);
    modal.show();

    return () => {
      modalEl.removeEventListener('hidden.bs.modal', handleHidden);
      modal.dispose?.();
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const modalEl = modalRef.current;
    const TooltipCtor = window.bootstrap?.Tooltip;
    if (!modalEl || !TooltipCtor) {
      return undefined;
    }

    const tooltips = Array.from(
      modalEl.querySelectorAll('[data-bs-toggle="tooltip"]'),
    ).map((element) => new TooltipCtor(element, {
      container: modalEl,
      trigger: 'hover focus',
    }));

    return () => {
      tooltips.forEach((tooltip) => tooltip.dispose?.());
    };
  }, [isOpen, needsRegularScope, request?.mode, shiftTimeReference]);

  useEffect(() => {
    if (
      !isOpen
      || !Number.isInteger(parsedDelta)
      || activeTimeDraftInvalid
      || !deltaWithinBounds
    ) {
      return;
    }

    onPreviewDeltaChange?.(parsedDelta);
  }, [
    activeTimeDraftInvalid,
    deltaWithinBounds,
    isOpen,
    onPreviewDeltaChange,
    parsedDelta,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleTimelineShiftCopyCreated(event) {
      if (busy || !isTimelineShiftCopyCreatedEvent(event)) {
        return;
      }

      hideTimelineShiftModal(modalRef.current, onClose);
    }

    document.addEventListener('crud:created', handleTimelineShiftCopyCreated);
    return () => {
      document.removeEventListener('crud:created', handleTimelineShiftCopyCreated);
    };
  }, [busy, isOpen, onClose]);

  if (!request || typeof document === 'undefined') {
    return null;
  }

  function requestClose() {
    if (busy) {
      return;
    }

    hideTimelineShiftModal(modalRef.current, onClose);
  }

  function updateDeltaFromTargetTime(edge, value) {
    setTimeDrafts((current) => ({
      ...current,
      [edge]: value,
    }));

    if (!shiftTimeReference) {
      return;
    }

    const targetMinutes = parseTimeValue(value);
    if (!Number.isInteger(targetMinutes)) {
      return;
    }

    const sourceMinutes = edge === 'end'
      ? shiftTimeReference.endMinutes
      : shiftTimeReference.startMinutes;
    setDeltaValue(String(targetMinutes - sourceMinutes));
  }

  function handleTargetTimeFocus(edge, value) {
    setActiveTimeField(edge);
    setTimeDrafts((current) => ({
      ...current,
      [edge]: value,
    }));
  }

  function handleTargetTimeBlur() {
    setActiveTimeField(null);
    setTimeDrafts({ end: '', start: '' });
  }

  async function submitTimelineShift(action, options = {}) {
    if (!canSubmit) {
      return;
    }

    const submitted = await onSubmit?.({
      action,
      deltaMinutes: parsedDelta,
      regularScope: needsRegularScope ? regularScope : 'day',
      copyTarget: options.copyTarget ?? 'day-workflow',
    });

    if (submitted && action !== 'copy') {
      requestClose();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    await submitTimelineShift('shift');
  }

  const copyControls = canCopyShiftedWorkflow ? (
    sourceIsRegularWorkflow ? (
      <div className="btn-group timeline-shift-copy-group">
        <button
          type="button"
          className="btn btn-outline-light dropdown-toggle timeline-shift-copy-toggle"
          disabled={!canSubmit}
          data-bs-toggle="dropdown"
          aria-expanded="false"
          data-testid="timeline-shift-copy"
        >
          Kopie vorbereiten
        </button>
        <div className="dropdown-menu dropdown-menu-end timeline-shift-copy-menu">
          <button
            type="button"
            className="dropdown-item"
            disabled={!canSubmit}
            data-testid="timeline-shift-copy-day-workflow"
            onClick={() => submitTimelineShift('copy', { copyTarget: 'day-workflow' })}
          >
            <span className="timeline-shift-copy-menu-title">
              Als Tages-Workflow kopieren
            </span>
            <span className="timeline-shift-copy-menu-description">
              Erstellt daraus eine eigenständige Kopie für diesen Tag.
            </span>
          </button>
          <button
            type="button"
            className="dropdown-item"
            disabled={!canSubmit}
            data-testid="timeline-shift-copy-routine"
            onClick={() => submitTimelineShift('copy', { copyTarget: 'routine' })}
          >
            <span className="timeline-shift-copy-menu-title">
              Als Routine kopieren
            </span>
            <span className="timeline-shift-copy-menu-description">
              Erstellt eine neue Routine mit denselben Einstellungen.
            </span>
          </button>
        </div>
      </div>
    ) : (
      <button
        type="button"
        className="btn btn-outline-light timeline-shift-copy-toggle"
        disabled={!canSubmit}
        title="Tages-Workflow-Kopie vorbereiten"
        data-testid="timeline-shift-copy-day-workflow"
        onClick={() => submitTimelineShift('copy', { copyTarget: 'day-workflow' })}
      >
        Tageskopie vorbereiten
      </button>
    )
  ) : null;

  const modalNode = (
    <div
      ref={modalRef}
      className="modal fade timeline-shift-modal"
      data-testid="timeline-shift-modal"
      tabIndex="-1"
      aria-labelledby="timelineShiftModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <form
          className="modal-content bg-dark text-light border-secondary"
          data-testid="timeline-shift-form"
          onSubmit={handleSubmit}
        >
          <div className="modal-header border-secondary">
            <div>
              <div className="timeline-shift-eyebrow">Timeline</div>
              <h5 id="timelineShiftModalLabel" className="modal-title mb-0 timeline-shift-title">
                {buildTitle(request)}
                {request?.mode === 'day' ? (
                  <InfoTooltipButton
                    label="Hinweis zum Verschieben des Tages"
                    title={dayModeHelp}
                  />
                ) : null}
              </h5>
            </div>

            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Schließen"
              onClick={requestClose}
              disabled={busy}
              data-testid="timeline-shift-close"
            />
          </div>

          <div className="modal-body d-grid gap-3">
            <p className="text-secondary mb-0">{buildIntro(request)}</p>

            {shiftTimeReference ? (
              <div className="timeline-shift-time-summary" aria-live="polite">
                <span className="timeline-shift-time-summary-label">Zeitspanne</span>
                <span className="timeline-shift-time-summary-value">
                  {sourceTimeRangeLabel}
                </span>
                <i className="bi bi-arrow-right timeline-shift-time-summary-arrow" aria-hidden="true" />
                <span className="timeline-shift-time-summary-value is-target">
                  {targetTimeRangeLabel}
                </span>
              </div>
            ) : null}

            <div className="timeline-shift-delta-section">
              <label className="form-label timeline-shift-field-label" htmlFor="timelineShiftDeltaDirect">
                <span>Verschieben um</span>
                <InfoTooltipButton
                  label="Hinweis zur Minutenverschiebung"
                  title={deltaHelp}
                />
              </label>
              <div className="timeline-shift-delta-control">
                <input
                  id="timelineShiftDeltaDirect"
                  className={[
                    'form-control',
                    shiftOutOfBounds ? 'is-invalid' : '',
                  ].filter(Boolean).join(' ')}
                  type="number"
                  min="-720"
                  max="720"
                  step="1"
                  value={deltaValue}
                  onChange={(event) => {
                    setActiveTimeField(null);
                    setTimeDrafts({ end: '', start: '' });
                    setDeltaValue(event.target.value);
                  }}
                  disabled={busy}
                  required
                  data-testid="timeline-shift-delta"
                />
                <span className="timeline-shift-delta-unit">Minuten</span>
              </div>
            </div>

            {shiftTimeReference ? (
              <div className="timeline-shift-target-section">
                <div className="timeline-shift-target-header">
                  <span>Neue Zielzeit</span>
                  <InfoTooltipButton
                    label="Hinweis zur neuen Zielzeit"
                    title={targetTimeHelp}
                  />
                </div>
                <div className="timeline-shift-target-grid">
                  <div className="timeline-shift-field">
                    <label className="form-label" htmlFor="timelineShiftStartTime">
                      Start
                    </label>
                    <input
                      id="timelineShiftStartTime"
                      className={[
                        'form-control',
                        (activeTimeField === 'start' && activeTimeDraftInvalid) || shiftOutOfBounds
                          ? 'is-invalid'
                          : '',
                      ].filter(Boolean).join(' ')}
                      type="time"
                      value={displayedStartValue}
                      onFocus={() => handleTargetTimeFocus('start', displayedStartValue)}
                      onChange={(event) => updateDeltaFromTargetTime('start', event.target.value)}
                      onBlur={handleTargetTimeBlur}
                      disabled={busy}
                      data-testid="timeline-shift-start-time"
                    />
                  </div>
                  <div className="timeline-shift-field">
                    <label className="form-label" htmlFor="timelineShiftEndTime">
                      Ende
                    </label>
                    <input
                      id="timelineShiftEndTime"
                      className={[
                        'form-control',
                        (activeTimeField === 'end' && activeTimeDraftInvalid) || shiftOutOfBounds
                          ? 'is-invalid'
                          : '',
                      ].filter(Boolean).join(' ')}
                      type="time"
                      value={displayedEndValue}
                      onFocus={() => handleTargetTimeFocus('end', displayedEndValue)}
                      onChange={(event) => updateDeltaFromTargetTime('end', event.target.value)}
                      onBlur={handleTargetTimeBlur}
                      disabled={busy}
                      data-testid="timeline-shift-end-time"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {validationMessage ? (
              <div className="timeline-shift-validation" role="alert">
                {validationMessage}
              </div>
            ) : null}

            {needsRegularScope ? (
              <fieldset className="timeline-shift-scope">
                <legend className="form-label mb-2 timeline-shift-field-label">
                  <span>Verschieben</span>
                  <InfoTooltipButton
                    label="Hinweis zur Verschiebe-Auswahl"
                    title={scopeHelp}
                  />
                </legend>

                {mixedWorkflowSelection ? (
                  <p className="timeline-shift-scope-context">
                    Tages-Workflows werden einmalig verschoben. Für Routinen wählst du,
                    ob nur das Vorkommen oder die ganze Routine betroffen ist.
                  </p>
                ) : null}

                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="timelineShiftScope"
                    value="day"
                    checked={regularScope === 'day'}
                    onChange={() => setRegularScope('day')}
                    disabled={busy}
                    data-testid="timeline-shift-scope-day"
                  />
                  <span className="form-check-label">
                    {regularScopeLabels.day}
                  </span>
                </label>

                <label className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="timelineShiftScope"
                    value="rule"
                    checked={regularScope === 'rule'}
                    onChange={() => setRegularScope('rule')}
                    disabled={busy}
                    data-testid="timeline-shift-scope-rule"
                  />
                  <span className="form-check-label">
                    {regularScopeLabels.rule}
                  </span>
                </label>

              </fieldset>
            ) : null}
          </div>

          <div className="modal-footer border-secondary timeline-shift-footer">
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={requestClose}
              disabled={busy}
              data-testid="timeline-shift-cancel"
            >
              Abbrechen
            </button>
            <div className="timeline-shift-footer-actions">
              {copyControls}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSubmit}
                data-testid="timeline-shift-submit"
              >
                {shiftSubmitLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalNode, document.body);
}
